import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, X, ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";
import { useShop } from "../store/ShopContext";
import { api, formatPrice, CATEGORY_NAMES } from "../lib/api";
import { PageHeader } from "../components/PageHeader";

export default function Wishlist() {
  const { wishlist, toggleWishlist, addToCart } = useShop();
  const [products, setProducts] = useState({});

  useEffect(() => {
    const missing = wishlist.filter((w) => !products[w.slug]);
    if (missing.length === 0) return;
    Promise.all(missing.map((w) => api.get(`/products/${w.slug}`).then((r) => r.data.product).catch(() => null))).then((list) => {
      setProducts((prev) => ({ ...prev, ...Object.fromEntries(list.filter(Boolean).map((p) => [p.slug, p])) }));
    });
  }, [wishlist, products]);

  const total = wishlist.reduce((s, w) => s + w.price, 0);

  return (
    <div data-testid="wishlist-page">
      <PageHeader eyebrow="Favorite" title="Lista ta de dorințe" description={wishlist.length ? `${wishlist.length} produse salvate · valoare totală ${formatPrice(total)}` : "Salvează modelele care te inspiră și revino oricând."} crumbs={[{ label: "Favorite" }]} testId="wishlist-header" />
      <div className="container-cc">
        {wishlist.length === 0 ? (
          <div data-testid="wishlist-empty" className="surface py-24 text-center">
            <Heart size={36} strokeWidth={1} className="mx-auto text-[#B4A38E] mb-5" />
            <p className="font-serif text-3xl">Nicio favorită încă</p>
            <p className="text-sm text-[#737373] mt-2 max-w-sm mx-auto">Atinge inima de pe orice produs pentru a-l păstra aici.</p>
            <Link to="/catalog" className="btn-primary mt-8">Descoperă colecția</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {wishlist.map((w, i) => {
              const p = products[w.slug];
              return (
                <motion.div key={w.slug} data-testid={`wishlist-item-${w.slug}`} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="surface p-5 flex gap-5 group">
                  <Link to={`/produs/${w.slug}`} className="bg-white w-32 h-36 sm:w-40 sm:h-44 flex items-center justify-center p-3 shrink-0 overflow-hidden">
                    <img src={w.image} alt={w.name} className="product-img max-h-full object-contain transition-transform duration-500 group-hover:scale-105" />
                  </Link>
                  <div className="flex-1 flex flex-col min-w-0">
                    <div className="flex justify-between gap-3">
                      <div className="min-w-0">
                        <p className="eyebrow">{CATEGORY_NAMES[w.category]}</p>
                        <Link to={`/produs/${w.slug}`} className="font-serif text-xl leading-tight block mt-1 hover:text-[#B4A38E] transition-colors">{w.name}</Link>
                        {w.color && <p className="text-xs text-[#737373] mt-0.5">{w.color}</p>}
                      </div>
                      <button data-testid={`wishlist-remove-${w.slug}`} aria-label="Elimină" onClick={() => toggleWishlist(w)} className="h-fit text-[#737373] hover:text-[#0A0A0A]"><X size={18} strokeWidth={1.5} /></button>
                    </div>
                    <p className="font-semibold mt-3">{formatPrice(w.price)}</p>
                    <button data-testid={`wishlist-add-to-cart-${w.slug}`} disabled={!p} onClick={() => addToCart(p, p.color)} className="btn-primary mt-auto h-10 px-4 text-[11px] self-start"><ShoppingBag size={14} /> Adaugă în coș</button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
